import { useEffect, useState } from "react";
import { Copy, ImagePlus, Palette } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyBrandColor } from "@/lib/branding";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const BrandingPanel = () => {
  const { me, refresh } = useAuth();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/branding").then((r) => setF({ ...r.data, color: r.data.color || "", quote_lines: [...(r.data.quote_lines || []), "", ""].slice(0, 2), logoChanged: false })).catch(() => {}); }, []);
  if (!f || me?.role?.key !== "super_admin" || me?.is_demo_user) return null;
  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error("Logo troppo grande (max 400 KB)."); return; }
    const rd = new FileReader();
    rd.onload = () => setF({ ...f, logo: rd.result, logoChanged: true });
    rd.readAsDataURL(file);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/branding", { name: f.name || "", color: f.color || "", quote_lines: f.quote_lines.filter(Boolean), ...(f.logoChanged ? { logo: f.logo || "" } : {}) });
      toast.success("Tema aggiornato"); refresh();
    } catch (x) { toast.error(errorMessage(x)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 lg:col-span-3" data-testid="branding-panel">
      <div className="mb-2 flex items-center gap-2"><Palette className="h-4 w-4 text-slate-400" /><SectionLabel>Tema, logo e carta intestata delle offerte</SectionLabel></div>
      <p className="mb-4 text-xs text-slate-500">Valgono solo per la tua azienda: colore dell'interfaccia, logo in alto a sinistra e intestazione/piè di pagina dei PDF delle offerte. Lascia vuoto per i valori standard.</p>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1.5"><Label className="text-xs">Nome visualizzato</Label><Input maxLength={80} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="branding-name" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Colore principale</Label>
          <div className="flex gap-2"><input type="color" value={f.color || "#ff6400"} onChange={(e) => { setF({ ...f, color: e.target.value }); applyBrandColor(e.target.value); }} className="h-9 w-12 cursor-pointer rounded border border-line" data-testid="branding-color" />
            <Input value={f.color} placeholder="#FF6400" maxLength={7} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="branding-color-hex" /></div></div>
        <div className="space-y-1.5"><Label className="text-xs">Logo (PNG/JPG, max 400 KB)</Label>
          <div className="flex items-center gap-2">
            {f.logo ? <img src={f.logo} alt="Logo" className="h-9 max-w-[120px] rounded border border-line object-contain p-1" data-testid="branding-logo-preview" /> : <span className="text-xs text-slate-400">Logo standard</span>}
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-control border border-line px-2 py-1.5 text-xs font-semibold hover:bg-slate-50"><ImagePlus className="h-3.5 w-3.5" />Carica<input type="file" accept="image/png,image/jpeg" className="hidden" onChange={pickLogo} data-testid="branding-logo-input" /></label>
            {f.logo && <Button size="sm" variant="ghost" onClick={() => setF({ ...f, logo: null, logoChanged: true })} data-testid="branding-logo-remove">Rimuovi</Button>}
          </div></div>
        {[0, 1].map((i) => <div key={i} className="space-y-1.5 md:col-span-3"><Label className="text-xs">Piè di pagina offerte · riga {i + 1} {i === 0 ? "(es. ragione sociale, indirizzo, telefono, email)" : "(es. P.IVA, REA, PEC)"}</Label>
          <Input maxLength={200} value={f.quote_lines[i]} onChange={(e) => { const q = [...f.quote_lines]; q[i] = e.target.value; setF({ ...f, quote_lines: q }); }} data-testid={`branding-quote-line-${i}`} /></div>)}
      </div>
      <Button className="mt-4" onClick={save} disabled={busy} data-testid="branding-save">{busy ? "Salvataggio…" : "Salva tema"}</Button>
      <LoginAddress slug={me?.tenant?.slug || "on"} />
    </Panel>
  );
};

const LoginAddress = ({ slug }) => {
  const url = `${window.location.origin}/accedi/${slug}`;
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Indirizzo copiato")).catch(() => toast.error("Copia non riuscita"));
  return (
    <div className="mt-5 border-t border-line pt-4" data-testid="branding-login-address">
      <Label className="text-xs">Indirizzo di accesso della tua azienda</Label>
      <p className="mb-2 text-xs text-slate-500">Si apre già con il vostro logo e colore: dallo ai tuoi colleghi.</p>
      <div className="flex gap-2"><Input readOnly value={url} className="font-mono text-xs" data-testid="branding-login-url" />
        <Button variant="outline" size="sm" onClick={copy} className="gap-1.5" data-testid="branding-login-copy"><Copy className="h-3.5 w-3.5" />Copia</Button></div>
    </div>
  );
};
